import type { PaymentMethod } from '@/types/checkout';
import type { StripeGooglePayRequest, StripePaymentParams } from '@/types/payment';
import type { BillingDetails, PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';

export const buildStripeGooglePayRequest = (
  paymentMethodData: StripePaymentMethod,
  providerSettings: PaymentMethod,
  billingDetails: BillingDetails
): StripeGooglePayRequest => {
  const card = paymentMethodData.card;
  if (!card) {
    throw new Error('Card data is missing from payment method');
  }

  const params: StripePaymentParams = {
    paymentMethodId: paymentMethodData.id,
    cardDetails: {
      bin: '',
      nameOnCard: billingDetails.name || '',
      lastFourDigits: card.last4,
      expirationMonth: card.exp_month.toString(),
      expirationYear: card.exp_year.toString(),
      cardBrand: card.brand,
      fingerprint: card.fingerprint || '',
      countryOfIssuance: card.country || '',
    },
    paymentMethodDetails: {
      id: paymentMethodData.id,
      object: 'payment_method',
      allow_redisplay: 'unspecified',
      billing_details: {
        address: {
          city: billingDetails.address?.city || null,
          country: billingDetails.address?.country || null,
          line1: billingDetails.address?.line1 || '',
          line2: billingDetails.address?.line2 || null,
          postal_code: billingDetails.address?.postal_code || '',
          state: billingDetails.address?.state || null,
        },
        email: billingDetails.email || '',
        name: billingDetails.name || null,
        phone: billingDetails.phone || null,
        tax_id: null,
      },
      card: {
        brand: card.brand,
        checks: {
          address_line1_check: card.checks?.address_line1_check || null,
          address_postal_code_check: card.checks?.address_postal_code_check || null,
          cvc_check: card.checks?.cvc_check || null,
        },
        country: card.country || '',
        display_brand: card.display_brand || card.brand,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        funding: card.funding,
        generated_from: card.wallet?.google_pay || null,
        last4: card.last4,
        networks: {
          available: card.networks?.available || [card.brand],
          preferred: card.networks?.preferred || null,
        },
        regulated_status: 'unregulated',
        three_d_secure_usage: {
          supported: card.three_d_secure_usage?.supported || true,
        },
        wallet: card.wallet
          ? {
              dynamic_last4: card.wallet.dynamic_last4 || card.last4,
              google_pay: card.wallet.google_pay || {},
              type: 'google_pay',
            }
          : null,
      },
      created: paymentMethodData.created,
      customer: paymentMethodData.customer,
      livemode: paymentMethodData.livemode,
      radar_options: {},
      type: 'card',
    },
    shouldSaveCard: false,
    fraudDetection: {
      forterTokenCookie: '',
    },
  };

  return {
    paymentProvider: 'stripe',
    paymentMethodId: providerSettings.id,
    paymentMethodDisplayName: 'Google Pay',
    receipt_email: paymentMethodData.billing_details?.email || '',
    params,
  };
};
